import * as z from 'zod';
import {
  BaseRepository,
  IBaseModel,
  IListFetchParams,
  MN_SKILLS,
  MODULE,
} from '../common';
import type { SkillType, Prisma } from '@prisma/client';
import { SkillTypeEnum } from '@jobcost-monorepo/database/prisma/enums';

type Skill = Prisma.$SkillPayload['scalars'];

export type ISkill = Skill & Omit<IBaseModel, 'createdAt' | 'updatedAt' | 'id'>;

export interface IListSkillFetchParams extends IListFetchParams {
  skillType?: SkillType;
}

class SkillRepository extends BaseRepository<ISkill, IListSkillFetchParams> {
  constructor(moduleName: MODULE) {
    super(moduleName);
    this.keyValueMapFields = {
      idlinkedField: 'id',
      descriptionlinkedField: ['name'],
    };
    this.gridFieldsOptions = [
      {
        visible: false,
        position: 0,
        linkedField: 'id',
        type: '',
        align: 'left',
        width: '12rem',
        sortable: false,
        formatType: 'string',
      },
      {
        visible: true,
        position: 1,
        linkedField: 'name',
        type: '',
        align: 'left',
        width: 'w-full',
        sortable: false,
        formatType: 'string',
      },
      {
        visible: true,
        position: 2,
        linkedField: 'skillType',
        type: '',
        align: 'left',
        width: 'w-full',
        sortable: false,
        formatType: 'string',
      },
    ];

    this.gridFiltersOptions = [
      {
        position: { x: 0, y: 0 },
        linkedField: 'skillType',
        width: 'w-full',
        formatType: 'dropdown',
        moduleName: MN_SKILLS,
        translationModuleName: MN_SKILLS,
        enumValues: SkillTypeEnum,
      },
    ];

    this.formFieldsOptions = [
      {
        visible: false,
        readOnly: true,
        position: { x: 0, y: 0 },
        linkedField: 'id',
        type: 'string',
        width: '12rem',
        formatType: 'input',
        moduleName: MN_SKILLS,
        translationModuleName: MN_SKILLS,
      },
      {
        visible: true,
        readOnly: false,
        position: { x: 1, y: 0 },
        linkedField: 'name',
        type: 'string',
        width: 'w-full',
        formatType: 'input',
        moduleName: MN_SKILLS,
        translationModuleName: MN_SKILLS,
      },
      {
        visible: true,
        readOnly: false,
        position: { x: 0, y: 1 },
        linkedField: 'skillType',
        type: 'string',
        width: 'w-full',
        formatType: 'dropdown',
        moduleName: MN_SKILLS,
        translationModuleName: MN_SKILLS,
        enumValues: SkillTypeEnum,
      },
      {
        visible: true,
        readOnly: false,
        position: { x: 0, y: 2 },
        linkedField: 'note',
        type: 'string',
        width: 'w-full',
        formatType: 'textArea',
        moduleName: MN_SKILLS,
        translationModuleName: MN_SKILLS,
      },
    ];

    // @ts-ignore
    this.formValidationSchema = z.object({
      id: z.number().nullable().optional(),
      name: z
        .string({
          invalid_type_error: 'fieldRequired',
          required_error: 'fieldRequired',
        })
        .min(2, { message: 'min_2' })
        .max(30, { message: 'max_30' }),
      skillType: z.string({
        invalid_type_error: 'fieldRequired',
        required_error: 'fieldRequired',
      }),
      note: z
        .string()
        .min(2, { message: 'min_2' })
        .max(255, { message: 'max_255' })
        .nullable()
        .optional(),
    });
  }
}

export default SkillRepository;
